import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LiveTag, TeamAvatar, fmtDateTime, ThemeToggle } from "@/components/common";
import RefereeScoreboard from "@/components/RefereeScoreboard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Lock, Play, Flag } from "lucide-react";

function toHostMatch(m) {
  return { ...m, team_a_id: m.team_a?.id || null, team_b_id: m.team_b?.id || null };
}

export default function RefereeView() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [nf, setNf] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [starting, setStarting] = useState(false);
  const fetchVersion = useRef(0);

  const load = useCallback(async () => {
    const version = ++fetchVersion.current;
    try {
      const response = await api.get(`/access/referee/${code}`);
      if (version === fetchVersion.current) { setD(response.data); setNf(false); }
    } catch (error) {
      if (version === fetchVersion.current && error.response?.status === 404) setNf(true);
    }
  }, [code]);
  const onWriting = useCallback(() => { fetchVersion.current += 1; }, []);
  const onSaved = useCallback((updated) => {
    fetchVersion.current += 1;
    setD((previous) => previous ? { ...previous, matches: previous.matches.map((match) => match.id === updated.id ? { ...match, ...updated } : match) } : previous);
  }, []);
  const onFinal = useCallback(async (matchId) => {
    fetchVersion.current += 1;
    setD((previous) => previous ? { ...previous, matches: previous.matches.map((match) => match.id === matchId ? { ...match, status: "final" } : match) } : previous);
    setSelectedId(null);
    await load();
  }, [load]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [load]);

  if (nf) return <Center>Invalid referee link.</Center>;
  if (!d) return <Center>Loading…</Center>;

  const matches = d.matches.map(toHostMatch);
  const pending = matches.filter((m) => m.status !== "final");
  const ordered = [...pending].sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  const next = pending.find((match) => match.id === selectedId) || ordered.find((match) => match.status === "live") || ordered[0];
  const rest = pending.filter((m) => m.id !== next?.id);
  const done = matches.filter((m) => m.status === "final");
  const primary = "hsl(var(--primary))";

  const startMatch = async () => {
    setStarting(true);
    onWriting();
    try {
      const response = await api.post(`/access/referee/matches/${next.id}/start`, { code });
      onSaved(response.data);
      setSelectedId(next.id);
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    finally { setStarting(false); }
  };

  return (
    <div className="min-h-screen bg-background pb-10">
      <header className="flex items-center justify-between border-b p-4">
        <div className="flex items-center gap-2"><Flag className="h-5 w-5" style={{ color: primary }} /><div><div className="font-heading font-bold leading-tight">{d.referee.name}</div><div className="text-xs text-muted-foreground">Referee · {d.tournament.name}</div></div></div>
        <ThemeToggle />
      </header>

      <div className="mx-auto max-w-2xl space-y-6 p-4">
        <div>
          <div className="mb-2 text-label text-muted-foreground">{next?.status === "live" ? "Live match" : "Next match"}</div>
          {next?.status === "live" ? (
            <RefereeScoreboard key={`${code}-${next.id}`} match={next} code={code} allowResults={d.allow_results}
              onWriting={onWriting} onSaved={onSaved} onFinal={onFinal} />
          ) : next ? (
            <Card className="mt-2 overflow-hidden">
              <div className="p-5">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {fmtDateTime(next.scheduled_time, next.timezone)}</span>
                  <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {next.field || "—"}</span>
                </div>
                <div className="mt-4 space-y-3">
                  <BigTeam team={next.team_a} ph={next.team_a_placeholder} />
                  <div className="text-center text-xs font-bold text-muted-foreground">VS</div>
                  <BigTeam team={next.team_b} ph={next.team_b_placeholder} />
                </div>
                {d.allow_results ? (
                  <Button className="mt-5 w-full" style={{ backgroundColor: primary }} disabled={starting || !next.team_a_id || !next.team_b_id}
                    onClick={startMatch} data-testid="referee-enter-result">
                    <Play className="mr-1.5 h-4 w-4" /> {starting ? "Starting…" : "Start match"}
                  </Button>
                ) : <p className="mt-4 text-center text-xs text-muted-foreground">Score entry is managed by the host.</p>}
              </div>
            </Card>
          ) : <Card className="mt-2 p-6 text-center text-muted-foreground">No upcoming matches assigned.</Card>}
        </div>

        {rest.length > 0 && (
          <div>
            <div className="text-label mb-2 text-muted-foreground">Your other matches</div>
            <div className="space-y-2">{rest.map((m) => <MiniMatch key={m.id} m={m} onClick={() => setSelectedId(m.id)} />)}</div>
          </div>
        )}
        {done.length > 0 && (
          <div>
            <div className="text-label mb-2 text-muted-foreground">Completed</div>
            <div className="space-y-2">{done.map((m) => <MiniMatch key={m.id} m={m} />)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="flex h-screen items-center justify-center text-muted-foreground">{children}</div>;

function BigTeam({ team, ph }) {
  return (
    <div className="flex items-center gap-3">
      {team ? <TeamAvatar team={team} size={40} /> : <div className="h-10 w-10 rounded-lg bg-muted" />}
      <span className="font-heading text-xl font-bold">{team ? team.name : ph || "TBD"}</span>
    </div>
  );
}
function MiniMatch({ m, onClick }) {
  const isFinal = m.status === "final";
  return (
    <Card className={`p-3 ${onClick ? "cursor-pointer" : ""}`} onClick={onClick} data-testid={`referee-match-${m.id}`}>
      <div className="mb-1 flex justify-between text-xs text-muted-foreground"><span>{m.round_name} · {m.field || "—"}</span>{isFinal ? <span className="flex items-center gap-1 text-emerald-600"><Lock className="h-3 w-3" /> FINAL</span> : m.status === "live" ? <LiveTag /> : null}</div>
      <div className="mb-2 flex items-center gap-1 text-xs text-muted-foreground" data-testid={`referee-match-time-${m.id}`}>
        <Clock className="h-3.5 w-3.5 shrink-0" />
        {m.scheduled_time ? fmtDateTime(m.scheduled_time, m.timezone) : "Time not scheduled"}
      </div>
      <div className="flex items-center justify-between"><span>{m.team_a?.name || m.team_a_placeholder || "TBD"}</span><span className="font-bold">{m.score_a ?? ""}</span></div>
      <div className="flex items-center justify-between"><span>{m.team_b?.name || m.team_b_placeholder || "TBD"}</span><span className="font-bold">{m.score_b ?? ""}</span></div>
    </Card>
  );
}
